import { Button, Modal, Select, Textarea } from "@mantine/core";
import { IconThumbDown, IconThumbUp } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { predictionApi } from "#/api/predictionApi";
import { showSuccessNotification } from "#/components/notifications";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import { FeedbackReason, type ShipmentSummary } from "#/types/prediction";
import { logger } from "#/utils/logger";

interface Props {
  shipment: ShipmentSummary;
  opened: boolean;
  onClose: () => void;
}

/**
 * Feedback on one shipment, the prediction as a whole: an optional comment,
 * then thumbs up or thumbs down. Down asks for a reason first. Stored with
 * the shipment and aggregated for TMA admins; the engine does not adjust
 * itself from it.
 */
const FeedbackModal = ({ shipment, opened, onClose }: Props) => {
  const t = usePrvTranslation();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState(shipment.feedbackComment ?? "");
  const [down, setDown] = useState(false);
  const [reason, setReason] = useState<FeedbackReason | null>(shipment.feedbackReason);

  useEffect(() => {
    if (opened) {
      setComment(shipment.feedbackComment ?? "");
      setReason(shipment.feedbackReason);
      setDown(false);
    }
  }, [opened, shipment.feedbackComment, shipment.feedbackReason]);

  const send = useMutation({
    mutationFn: (payload: { useful: boolean; reason?: FeedbackReason; comment?: string }) =>
      predictionApi.giveFeedback(shipment.id, payload),
    onSuccess: () => {
      showSuccessNotification({
        message: t("predictedDeliveries.feedback.saved"),
        color: "green.5",
      });
      void queryClient.invalidateQueries({ queryKey: ["shipment-detail"] });
      void queryClient.invalidateQueries({ queryKey: ["prediction-board"] });
      onClose();
    },
    onError: (error: Error) => {
      // The API client already toasts the server message.
      logger.error("FeedbackModal error:", error);
    },
  });

  const useful = shipment.feedbackUseful;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="md"
      title={t("predictedDeliveries.feedback.title")}
    >
      <div className="flex flex-col gap-4">
        <span className="text-sm text-gray-600">
          {useful === null
            ? t("predictedDeliveries.feedback.hint")
            : useful
              ? t("predictedDeliveries.feedback.markedUseful")
              : t("predictedDeliveries.feedback.markedNotUseful", {
                  reason: shipment.feedbackReason
                    ? t(`predictedDeliveries.feedback.reasons.${shipment.feedbackReason}`)
                    : "--",
                })}
        </span>

        <Textarea
          label={t("predictedDeliveries.feedback.commentLabel")}
          value={comment}
          onChange={(event) => setComment(event.currentTarget.value)}
          maxLength={500}
          autosize
          minRows={3}
        />

        {down && (
          <Select
            label={t("predictedDeliveries.feedback.reasonLabel")}
            data={Object.values(FeedbackReason).map((value) => ({
              value,
              label: t(`predictedDeliveries.feedback.reasons.${value}`),
            }))}
            value={reason}
            onChange={(value) => setReason(value as FeedbackReason | null)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: false }}
          />
        )}

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
          <Button
            variant={useful === true ? "filled" : "outline"}
            color="green"
            leftSection={<IconThumbUp size={18} />}
            loading={send.isPending && !down}
            onClick={() => send.mutate({ useful: true, comment: comment || undefined })}
          >
            {t("predictedDeliveries.feedback.useful")}
          </Button>
          {down ? (
            <Button
              color="red"
              leftSection={<IconThumbDown size={18} />}
              disabled={!reason}
              loading={send.isPending}
              onClick={() =>
                reason && send.mutate({ useful: false, reason, comment: comment || undefined })
              }
            >
              {t("predictedDeliveries.feedback.send")}
            </Button>
          ) : (
            <Button
              variant={useful === false ? "filled" : "outline"}
              color="red"
              leftSection={<IconThumbDown size={18} />}
              onClick={() => setDown(true)}
            >
              {t("predictedDeliveries.feedback.notUseful")}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FeedbackModal;
